/**
 * "Today I want to…" — the search box at the top of Home. Type what you want in plain words:
 * a thing to do ("write a report"), a person ("tell me about John Doe"), a car ("who drives car 104"),
 * today's supervisor, or a phone number ("on call DSS number"). Answers come from this agency's own records.
 */
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Icon } from '../shared/icons.tsx';
import { ErrorBox } from '../shared/ui.tsx';
import { at } from '../ops/common.tsx';

const tel = (p: string) => `tel:${p.replace(/[^\d+]/g, '')}`;
const Phone = ({ n }: { n: string | null | undefined }) => (n ? <a className="ask-tel" href={tel(n)}><Icon name="phone" size={15} /> {n}</a> : null);
const shortDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const until = (iso: string) => new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const titleCase = (s: string) => s.replace(/\b[a-z]/g, (c) => c.toUpperCase());

interface Answer {
  type: 'action' | 'car' | 'supervisors' | 'oncall' | 'phones' | 'staff' | 'person' | 'plate';
  [k: string]: any;
}

export function AskBox() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [answers, setAnswers] = useState<Answer[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const input = useRef<HTMLInputElement>(null);
  const go = (p: string) => navigate(at(p));

  const run = async (text: string) => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.get<{ answers: Answer[] }>(`/api/ask?q=${encodeURIComponent(t)}`);
      // Only one thing to do, nothing else to show: just go there.
      if (r.answers.length === 1 && r.answers[0]!.type === 'action') return go(r.answers[0]!.to);
      setAnswers(r.answers);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(q);
  };
  const clear = () => {
    setQ('');
    setAnswers(null);
    input.current?.focus();
  };

  return (
    <section className="ask" aria-label="Today I want to">
      <form className="ask-form" onSubmit={submit} role="search">
        <label htmlFor="ask-q" className="ask-label">
          Today I want to…
        </label>
        <div className="ask-row">
          <Icon name="search" size={20} />
          <input
            id="ask-q"
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="write a report, look up a person, find a phone number…"
            autoComplete="off"
            enterKeyHint="search"
          />
          {q && (
            <button type="button" className="ask-x" aria-label="Clear" onClick={clear}>
              ×
            </button>
          )}
          <button type="submit" className="btn primary" disabled={busy || !q.trim()}>
            {busy ? 'Looking…' : 'Go'}
          </button>
        </div>
      </form>
      <ErrorBox error={error} />
      {answers && (
        <div className="ask-out" aria-live="polite">
          {answers.length === 0 ? (
            <div className="ask-card">
              <strong>I didn’t catch that.</strong>
              <p className="muted small">
                Try “write a ticket”, “tell me about Jane Roe”, “who drives car 104”, “who’s the supervisor today” or “coroner phone number”.
              </p>
            </div>
          ) : (
            answers.map((a, i) => <AnswerCard key={i} a={a} go={go} />)
          )}
          <button type="button" className="btn link small" onClick={clear}>
            Clear
          </button>
        </div>
      )}
    </section>
  );
}

function Card({ title, icon, children, tone }: { title: ReactNode; icon: string; children: ReactNode; tone?: string }) {
  return (
    <div className={`ask-card ${tone ?? ''}`}>
      <div className="ask-h">
        <Icon name={icon} size={18} />
        <strong>{title}</strong>
      </div>
      {children}
    </div>
  );
}

function AnswerCard({ a, go }: { a: Answer; go: (p: string) => void }) {
  if (a.type === 'action')
    return (
      <button type="button" className="ask-card ask-action" onClick={() => go(a.to)}>
        <Icon name="plus" size={18} />
        <strong>{a.label}</strong>
        <span aria-hidden>›</span>
      </button>
    );

  if (a.type === 'car')
    return (
      <Card title={`Car ${a.unit}`} icon="car">
        {a.cars.length === 0 && <p className="muted">There’s no car {a.unit} on the fleet list.</p>}
        {a.cars.map((c: any) => (
          <div key={c.id} className="ask-line">
            <div className="small muted">
              {[c.year, c.make, c.model].filter(Boolean).join(' ')}
              {c.color ? `, ${c.color}` : ''}
              {c.tag ? ` · ${c.tag_state ?? ''} ${c.tag}` : ''}
              {c.status === 'out_of_service' ? ' · Out of service' : c.status === 'spare' ? ' · Spare' : ''}
            </div>
            {c.temp ? (
              <div>
                <strong>{c.temp}</strong> has it now{c.temp_call ? ` (${c.temp_call})` : ''} <Phone n={c.temp_phone} />
                {c.assigned && <div className="small muted">Normally assigned to {c.assigned}</div>}
              </div>
            ) : c.assigned ? (
              <div>
                <strong>{c.assigned}</strong>
                {c.assigned_call ? ` (${c.assigned_call})` : ''} <Phone n={c.assigned_phone} />
              </div>
            ) : (
              <div className="muted">Not assigned to anyone.</div>
            )}
            {a.canOpen && (
              <button type="button" className="btn link small" onClick={() => go(`/fleet/${c.id}`)}>
                Open the car’s record ›
              </button>
            )}
          </div>
        ))}
      </Card>
    );

  if (a.type === 'supervisors')
    return (
      <Card title="Supervisor today" icon="user">
        {a.shifts.length === 0 && <p className="muted">No supervisor is set up yet.</p>}
        {a.shifts.map((s: any, i: number) => (
          <div key={i} className="ask-line">
            <div>
              <strong>{s.name}</strong> {s.onDuty && <span className="badge ok">On duty now</span>} {s.offToday && <span className="badge warn">Off today</span>}
            </div>
            <div className="small muted">
              {[s.shift, s.hours, s.callNumber && `Call ${s.callNumber}`].filter(Boolean).join(' · ')}
            </div>
            <Phone n={s.phone} />
          </div>
        ))}
      </Card>
    );

  if (a.type === 'oncall')
    return (
      <Card title="On-call magistrate" icon="gavel">
        {a.now.length === 0 ? (
          <p className="muted">No one is listed as on call right now.</p>
        ) : (
          a.now.map((o: any) => (
            <div key={o.id} className="ask-line">
              <strong>{o.name}</strong> {o.court && <span className="small muted">· {o.court}</span>}
              <div className="small muted">Until {until(o.ends_at)}</div>
              <Phone n={o.phone} />
            </div>
          ))
        )}
        {a.next && <div className="small muted">Next: {a.next.name}, from {until(a.next.starts_at)}</div>}
        <button type="button" className="btn link small" onClick={() => go('/on-call')}>
          On-call list ›
        </button>
      </Card>
    );

  if (a.type === 'phones')
    return (
      <Card title="Phone numbers" icon="phone">
        {a.items.length === 0 && <p className="muted">Nothing for “{a.terms.join(' ')}” on the agency’s phone list.</p>}
        {a.items.map((p: any) => (
          <div key={p.id} className="ask-line ask-phone">
            <div>
              <strong>{p.name}</strong>
              <div className="small muted">
                {p.category}
                {p.notes ? ` · ${p.notes}` : ''}
              </div>
            </div>
            <Phone n={p.phone} />
          </div>
        ))}
        <button type="button" className="btn link small" onClick={() => go('/phones')}>
          All important numbers ›
        </button>
      </Card>
    );

  if (a.type === 'staff')
    return (
      <Card title="Co-workers" icon="people">
        {a.items.map((u: any) => (
          <div key={u.id} className="ask-line ask-phone">
            <div>
              <strong>{u.name}</strong>
              <div className="small muted">{[u.title, u.call_number && `Call ${u.call_number}`, u.car && `Car ${u.car}`].filter(Boolean).join(' · ')}</div>
            </div>
            <Phone n={u.phone} />
          </div>
        ))}
      </Card>
    );

  if (a.type === 'plate')
    return (
      <Card title={`Tag ${a.plate}`} icon="car">
        {!a.intel.length && !a.tickets.length && !a.tows.length && <p className="muted">Nothing on file here for that tag. Run it through your terminal for DMV / NCIC.</p>}
        <IntelSec intel={a.intel} go={go} />
        {a.tickets.length > 0 && (
          <div className="ask-sec">
            <div className="ask-sub">Tickets</div>
            {a.tickets.map((t: any) => (
              <button key={t.id} type="button" className="ask-item" onClick={() => go(`/tickets/${t.id}`)}>
                <span>
                  {t.person_name} · {shortDate(t.issued_at)} <span className="small muted">· {t.location}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        {a.tows.length > 0 && (
          <div className="ask-sec">
            <div className="ask-sub">Tows</div>
            {a.tows.map((t: any) => (
              <button key={t.id} type="button" className="ask-item" onClick={() => go(`/tows/${t.id}`)}>
                <span>
                  {t.number} · {shortDate(t.towed_at)} <span className="small muted">· {t.status}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>
    );

  // A person.
  const name = titleCase(a.name);
  if (a.empty)
    return (
      <Card title={name} icon="user">
        <p className="muted">Nothing on file here for {name}: no active warrants, open civil papers, reports, tickets or tows.</p>
        <p className="small muted">These are this agency’s records only. Run NCIC and DMV through your terminal.</p>
      </Card>
    );
  return (
    <Card title={name} icon="user" tone={a.warrants.length ? 'bad' : ''}>
      {a.warrants.length > 0 && (
        <div className="ask-sec">
          <div className="ask-sub bad">Active warrant{a.warrants.length > 1 ? 's' : ''}</div>
          {a.warrants.map((w: any) => (
            <button key={w.id} type="button" className="ask-item" onClick={() => go(`/warrants/${w.id}`)}>
              <span>
                <strong>{w.name}</strong> · {titleCase(w.kind)} warrant {w.number}
                {w.charges.length > 0 && <span className="small muted"> · {w.charges.join(', ')}</span>}
              </span>
              {w.cautions.map((c: string) => (
                <span key={c} className="badge bad">
                  {c}
                </span>
              ))}
            </button>
          ))}
        </div>
      )}
      {a.papers.length > 0 && (
        <div className="ask-sec">
          <div className="ask-sub">Open civil papers</div>
          {a.papers.map((p: any) => (
            <button key={p.id} type="button" className="ask-item" onClick={() => go(`/civil-search?q=${encodeURIComponent(a.name)}`)}>
              <span>
                <strong>{p.defendant}</strong> · {p.type}
                <span className="small muted"> · {p.address}{p.deputy ? ` · ${p.deputy}` : ''}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {a.people.length > 0 && (
        <div className="ask-sec">
          <div className="ask-sub">In our records</div>
          {a.people.map((p: any) => (
            <button key={p.key} type="button" className="ask-item" onClick={() => go(`/people/profile?key=${encodeURIComponent(p.key)}`)} disabled={!a.canPeople}>
              <span>
                <strong>{p.name}</strong>
                {p.dobs?.length ? <span className="small muted"> · DOB {p.dobs.map((d: string) => shortDate(`${d}T12:00:00`)).join(' / ')}</span> : null}
                <span className="small muted">
                  {' · '}
                  {[p.reports && `${p.reports} report${p.reports > 1 ? 's' : ''}`, p.arrests && `${p.arrests} arrest${p.arrests > 1 ? 's' : ''}`, p.papers && `${p.papers} civil paper${p.papers > 1 ? 's' : ''}`].filter(Boolean).join(', ')}
                  {p.lastAddress ? ` · last at ${p.lastAddress}` : ''}
                </span>
              </span>
              <span aria-hidden>›</span>
            </button>
          ))}
        </div>
      )}
      {a.tickets.length > 0 && (
        <div className="ask-sec">
          <div className="ask-sub">Tickets</div>
          {a.tickets.map((t: any) => (
            <button key={t.id} type="button" className="ask-item" onClick={() => go(`/tickets/${t.id}`)}>
              <span>
                {t.kind === 'warning' ? 'Warning' : `Citation ${t.ticket_number ?? ''}`} · {shortDate(t.issued_at)}
                <span className="small muted"> · {t.location}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {a.tows.length > 0 && (
        <div className="ask-sec">
          <div className="ask-sub">Tows</div>
          {a.tows.map((t: any) => (
            <button key={t.id} type="button" className="ask-item" onClick={() => go(`/tows/${t.id}`)}>
              <span>
                {t.number} · {[t.vehicle?.year, t.vehicle?.make, t.vehicle?.model].filter(Boolean).join(' ')} · {shortDate(t.towed_at)}
                <span className="small muted"> · {t.status}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      <IntelSec intel={a.intel ?? []} go={go} />
      <p className="small muted">This agency’s records only. Run NCIC and DMV through your terminal.</p>
    </Card>
  );
}

function IntelSec({ intel, go }: { intel: any[]; go: (p: string) => void }) {
  if (!intel.length) return null;
  return (
    <div className="ask-sec">
      <div className="ask-sub">Field intel</div>
      {intel.map((f: any) => (
        <button key={f.id} type="button" className="ask-item" onClick={() => go(`/intel/${f.id}`)}>
          <span>
            <strong>{f.category}</strong> · {shortDate(f.observed_at)}
            <span className="small muted"> · {[f.location, f.what].filter(Boolean).join(' · ').slice(0, 140)} · {f.by_name}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
